import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

internal val TOUR_WORKFLOW_STEPS = listOf(
    "prihlasovani" to "Přihlašování", "prezentace" to "Prezentace", "kvalifikace" to "Kvalifikace", "vpp" to "VPP",
    "semifinale" to "Semifinále", "finale" to "Finále a uzavření",
)

internal fun tourRegistrationOpen(t: TourTournamentDto) = t.status == "open" && !t.presentationStarted

internal fun tourWorkflowDefaultStep(t: TourTournamentDto) = when {
    t.status == "finished" || t.playPhase in listOf("final", "finished") -> "finale"
    t.playPhase == "semifinal" -> "semifinale"
    tourRegistrationOpen(t) -> "prihlasovani"
    else -> "prezentace"
}

/** Old bookmarks enter the same tournament context, without maintaining a second workflow. */
@Composable
internal fun TourWorkflowEntry(token: String, step: String, navigate: (String) -> Unit) {
    TourSeasonScope(token, showSeason = false, includeFinished = true) { _, tournaments ->
        val selected = tournaments.firstOrNull { it.status == "running" }
            ?: (if (step in listOf("prezentace", "prihlasovani")) tournaments.firstOrNull { it.status != "finished" } else null)
            ?: tournaments.firstOrNull()
        LaunchedEffect(selected?.id) {
            selected?.let { navigate("admin/turnaj/${it.id}/$step") }
        }
        if (selected == null) Div(attrs = { classes("card") }) { P { Text("Nejprve založte turnaje v záložce Ročníky.") } }
    }
}

@Composable
internal fun TourTournamentWorkflow(token: String, id: String, requestedStep: String, navigate: (String) -> Unit) {
    var tournament by remember(id) { mutableStateOf<TourTournamentDto?>(null) }
    var season by remember(id) { mutableStateOf<TourSeasonDto?>(null) }
    var play by remember(id) { mutableStateOf<TourPlayBundle?>(null) }
    var slots by remember(id) { mutableStateOf<List<TourSlotDto>>(emptyList()) }
    var entries by remember(id) { mutableStateOf<List<TourEntryDto>>(emptyList()) }
    var refresh by remember(id) { mutableStateOf(0) }
    var error by remember(id) { mutableStateOf<String?>(null) }
    var loading by remember(id) { mutableStateOf(true) }
    val step = requestedStep.takeIf { value -> TOUR_WORKFLOW_STEPS.any { it.first == value } } ?: "prezentace"
    fun go(next: String) { navigate("admin/turnaj/$id/$next") }
    LaunchedEffect(id, refresh) {
        loading = true; error = null
        try {
            coroutineScope {
                val t = TourOperationsApi.tournament(token, id)
                val seasons = async { TourApi.seasons(token) }
                val games = async { TourPlayApi.bundle(token, id) }
                val rounds = async { TourOperationsApi.slots(token, id) }
                val players = async { TourOperationsApi.entries(token, id) }
                season = seasons.await().firstOrNull { it.id == t.seasonId }
                play = games.await(); slots = rounds.await(); entries = players.await(); tournament = t
            }
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    val t = tournament
    Div(attrs = { classes("card", "tour-workflow-header") }) {
        Button(attrs = { onClick { navigate("admin") } }) { Text("← Turnaje") }
        H2 { Text(t?.let { "Průběh turnaje · ${it.title}" } ?: "Průběh turnaje") }
        if (t != null) {
            P(attrs = { classes("muted") }) { Text(listOfNotNull(season?.name, t.startsOn?.let(::formatDate), tourLabel(t.discipline, TOUR_DISCIPLINES)).joinToString(" · ")) }
            P { Text(if (t.status == "finished") "Turnaj je uzavřený. Výsledky jsou zveřejněné." else if (t.presentationStarted && t.playPhase == "qualification" && slots.none { it.startedAt != null }) "Probíhá prezentace." else if (t.status == "running") "Probíhá · ${tourLabel(t.playPhase, TOUR_PHASES)}" else "Příprava turnaje · jednotlivé rundy zahájíte po prezentaci a losování.") }
        }
        TourError(error)
        if (error != null) Button(attrs = { onClick { refresh++ } }) { Text("Načíst průběh znovu") }
        if (loading && t == null) P { Text("Načítám turnaj…") }
        Div(attrs = { classes("tour-workflow-steps"); attr("role", "navigation"); attr("aria-label", "Kroky průběhu turnaje") }) {
            TOUR_WORKFLOW_STEPS.forEachIndexed { index, (value, label) ->
                if (value != "prihlasovani" || (t != null && tourRegistrationOpen(t)))
                    Button(attrs = { if (step == value) { classes("primary"); attr("aria-current", "step") }; onClick { go(value) } }) { Text("$index. $label") }
            }
        }
        if (t?.status == "finished") A(href = tourBase() + "/vysledky/${t.slug}", attrs = { target(ATarget.Blank); attr("rel", "noopener") }) { Text("Otevřít veřejné výsledky ↗") }
    }
    if (t == null || play == null) return
    val qualificationRoundsStarted = slots.none { slot -> slot.startedAt == null && entries.any { it.slotId == slot.id && it.attendance in listOf("registered", "present") } }
    val qualificationReady = play!!.qualification.any { it.played > 0 } &&
        play!!.qualification.all { it.complete || it.withdrawnPhase != null } && qualificationRoundsStarted
    key(id, step) {
        when (step) {
            "prihlasovani" -> {
                if (tourRegistrationOpen(t)) TourRegistrationEditor(token, id, registrationMode = true) { refresh++ }
                else Div(attrs = { classes("card") }) {
                    P { Text("Přihlašování není otevřené. Stav turnaje můžete upravit v seznamu turnajů.") }
                    Button(attrs = { onClick { navigate("admin") } }) { Text("Zpět na turnaje") }
                }
            }
            "prezentace" -> {
                if (t.presentationStarted || t.status in listOf("running", "finished")) {
                    TourRegistrationEditor(token, id, registrationMode = false) { refresh++ }
                    Div(attrs = { classes("tour-actions") }) { Button(attrs = { classes("primary"); onClick { go("kvalifikace") } }) { Text("Pokračovat na kvalifikaci") } }
                } else Div(attrs = { classes("card") }) {
                    P { Text("Prezentaci zahajte tlačítkem v seznamu turnajů. Přihlašování se tím uzavře.") }
                    Button(attrs = { onClick { navigate("admin") } }) { Text("Zpět na turnaje") }
                }
            }
            "vpp" -> {
                if (qualificationReady) TourVppEditor(token, id, season?.status == "finished") { refresh++ }
                else Div(attrs = { classes("card") }) {
                    H2 { Text("VPP po kvalifikaci") }
                    P { Text("Nejprve dokončete všechny obsazené kvalifikační rundy. Vylosovaný nához pak ověříme proti čistým kvalifikačním hrám.") }
                    Button(attrs = { onClick { go("kvalifikace") } }) { Text("Přejít na kvalifikaci") }
                }
                Div(attrs = { classes("tour-actions") }) { Button(attrs = { onClick { go("semifinale") } }) { Text("Pokračovat do semifinále") } }
            }
            else -> TourPlayEditor(token, id, when (step) { "semifinale" -> "semifinal"; "finale" -> "final"; else -> "qualification" },
                qualificationRoundsStarted = qualificationRoundsStarted,
                onChanged = { refresh++ }, onAdvanced = { next -> go(if (next == "semifinal") "vpp" else "finale") })
        }
    }
}
